// Packages
import React, { useState } from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import { Link, graphql } from "gatsby"
import styled, { css } from "styled-components"
import { CopyToClipboard } from "react-copy-to-clipboard"

// Components
import SuccessMessage from "@components/contact/success-message"
import SEO from "@components/global/seo"
// -Forms
import PaymentsForm from "@components/contact/payments-form"
import ComplaintsForm from "@components/contact/complaints-form"

const size = {
  tablet: 950,
  mobile: 600,
}

const above = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (min-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

const Wrapper = styled.div`
  margin: 0 24px;

  ${above.mobile`
  margin:0 48px;
  `}
`

const Header = styled.div`
  margin-top: 64px;

  h1 {
    font-family: "Playfair Display", serif;
    font-weight: 400;
    color: #1a202c;
    font-size: 44px;
    line-height: 60px;
    margin-bottom: 16px;

    ${above.mobile`
    font-size:56px;
    line-height:72px;
    text-align:center;
    `}
  }

  p {
    font-size: 20px;
    line-height: 28px;
    color: #2d3748;

    ${above.mobile`
    font-size:28px;
    line-height:36px;
    text-align:center;

    `}
  }
`
const SubNav = styled.div`
  margin-top: 64px;
  ${above.tablet`
  margin-top:80px;
  `}
`

const TabContainerWrapper = styled.div`
  margin: 0 24px;
`

const TabContainerMobile = styled.div`
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  max-width: 323px;
  ${above.mobile`
  display:none;
  `}
  .tab-general {
    ::before {
      visibility: ${props =>
        props.tabActive === "general" ? "visible" : "hidden"};
      transform: ${props =>
        props.tabActive === "general" ? "scaleX(1)" : "scaleX(0)"};
    }
  }
  .tab-payments {
    ::before {
      visibility: ${props =>
        props.tabActive === "payments" ? "visible" : "hidden"};
      transform: ${props =>
        props.tabActive === "payments" ? "scaleX(1)" : "scaleX(0)"};
    }
  }
  .tab-complaints {
    ::before {
      visibility: ${props =>
        props.tabActive === "complaints" ? "visible" : "hidden"};
      transform: ${props =>
        props.tabActive === "complaints" ? "scaleX(1)" : "scaleX(0)"};
    }
  }
`

const TabContainerTabletDesktop = styled(TabContainerMobile)`
  @media (max-width: 599px) {
    display: none;
  }

  ${above.mobile`
  display:flex;
  justify-content:center;
  max-width:unset;
  button {
    margin-right: 64px;
    &:last-of-type {
      margin-right: 0;
    }
  }
`}
`

const Tab = styled.button`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
  font-size: 20px;
  line-height: 28px;
  color: #1a202c;
  padding-bottom: 8px;
  position: relative;
  ::before {
    content: "";
    position: absolute;
    width: 100%;
    height: 2px;
    bottom: 0;
    left: 0;
    background-color: #0274e8;
    visibility: hidden;
    transform: scaleX(0);
    transition: 0.3s ease-in-out;
  }

  :hover {
    ::before {
      visibility: visible;
      transform: scaleX(1);
    }
  }
`

const SubNavHr = styled.hr`
  margin: 0;
  padding: 0;
  border-bottom: 1px solid #e2e8f0;
`

// General Tab section

const GeneralTab = styled.div`
  margin-top: 48px;

  h2 {
    font-family: "Playfair Display", serif;
    font-weight: 400;
    font-size: 36px;
    line-height: 44px;
    color: #2d3748;
    text-align: center;
    ${above.mobile`
    font-size:44px;
    line-height:60px;
    `}
  }
`

const CardContainer = styled.div`
  margin-top: 48px;
  display: flex;
  flex-direction: column;

  a {
    margin-bottom: 40px;

    ${above.mobile`
    margin-bottom:0;
    `}
  }
  ${above.mobile`
  flex-direction:row;
  justify-content:center;
  `}

  a:hover {
    text-decoration: none;
  }
  a:visited h3 {
    color: #1a202c;
  }

  .general-contact {
    flex: 1;
    ${above.mobile`
    margin-right: 40px;
    max-width:328px;
  `}
  }
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  transition: 0.3s ease-in-out;

  box-shadow:
    0px 8px 32px rgba(87, 99, 183, 0.1),
    0px 4px 8px rgba(119, 119, 119, 0.05);
  border-radius: 8px;
  &:visited h3 {
    color: inherit;
  }
  &:hover {
    transform: translateY(-8px);
    svg {
      fill: #f5fcff;
    }
  }

  &:hover {
    .card-footer {
      background: #f5fcff;
    }
  }
`

const CardImgText = styled.div`
  margin-top: 40px;
  padding: 0 32px;
  display: flex;
  flex-direction: column;
  align-items: center;

  h3 {
    margin-top: 40px;
    font-weight: 600;
    font-size: 22px;
    line-height: 28px;
    text-align: center;
    color: #1a202c;
  }

  p {
    margin: 4px 0 40px;
    font-size: 20px;
    line-height: 28px;
    color: #5c6677;
    text-align: center;
  }
`

const CardFooter = styled.div`
  width: 100%;
  background: #f7fafc;
  padding: 16px 0;
  p {
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    color: #013a74;
    text-align: center;
    margin-bottom: 0;
  }
`

const CardLink = styled(Link)`
  ${above.mobile`
  flex: 1;
  max-width: 328px;
`}
  .press {
    margin-right: 0;
  }
`
const Headquarters = styled.div`
  margin-top: 80px;
  position: relative;
  height: 400px;
  padding: 0 24px;

  ${above.mobile`
  padding:0 48px;
  `}
  ${above.tablet`
  padding:0 0;
  `}
`

const HeadquartersFlexContainer = styled.div`
  height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${above.tablet`
  max-width:1248px;
  margin:0 auto;
  `}
`

const HeadquartersCard = styled.div`
  background: white;
  width: 328px;
  padding: 40px 32px;
  border-radius: 4px;
  ${above.mobile`
  width:426px;
  padding:40px 48px;

  `}
  ${above.tablet`
  padding: 50px 48px;
  `}

  h2 {
    text-align: left;
    font-weight: 400;
    font-size: 36px;
    line-height: 44px;
    color: #1a202c;
    ${above.mobile`
    font-size:44px;
    line-height:60px;
    `}
  }

  hr {
    margin: 16px 0;
    margin-bottom: 16px;
    width: 88px;
    padding: 0;
    border-bottom: 2px solid #0274e8;
  }
  p {
    margin: 0;
    font-size: 20px;
    line-height: 28px;
    color: #1a202c;
    &:last-of-type {
      margin-bottom: 16px;
    }
  }
  a {
    font-weight: 600;
    font-size: 20px;
    line-height: 28px;
    color: #0274e8;
  }
`

const HeadquartersCardContainer = styled.div`
  ${above.tablet`
  max-width: 1248px;
  padding:0 48px;
`}
`

const PaymentsTab = styled.div``

const FormWrapper = styled.div`
  padding: 48px 24px;

  ${above.mobile`
padding:64px 48px;
`}

  ${above.tablet`
max-width:768px;
margin:0 auto;
`}
`

const WereHereToHelp = styled.div`
  background: #f7fafc;
  padding: 64px 24px;
  ${above.mobile`
  padding:64px 48px;
  `}

  ${above.tablet`
  padding:80px 48px;
  `}
  h2 {
    color: #1a202c;
    font-weight: 600;
    font-size: 28px;
    line-height: 36px;
    margin-bottom: 40px;
    text-align: center;

    ${above.mobile`
    margin-bottom:24px;
    `}
  }

  p {
    font-size: 16px;
    line-height: 20px;
    color: #5c6677;
    text-align: center;
    margin: 0;
  }
`

const FlexButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
  ${above.mobile`
flex-direction:row;
max-width:572px;
margin:0 auto 40px;
`}

  ${above.tablet`
max-width:612px;
`}
  a {
    color: #0274e8;
    width: 100%;
    background: white;
    border: 1px solid #e2e8f0;
    padding: 12px 0;
    font: inherit;
    font-size: 16px;
    line-height: 20px;
    font-weight: 600;
    cursor: pointer;
    outline: inherit;
    text-align: center;
    margin-bottom: 16px;
    &:hover {
      background: #0b395b;
      color: #b1d8fe;
    }

    ${above.mobile`
      margin-right:48px;
      margin-bottom:0;
      &:last-of-type {
        margin-right:0;
      }
    `}

    ${above.tablet`
    margin-right:88px;
    `}
    &:hover {
      text-decoration: none;
    }
  }

  .servicing-email {
    ${above.tablet`
      display:none;
    `}
  }

  .copy-button {
    position: relative;
    color: #0274e8;
    width: 100%;
    background: white;
    border: 1px solid #e2e8f0;
    padding: 12px 0;
    font: inherit;
    font-size: 16px;
    line-height: 20px;
    font-weight: 600;
    cursor: pointer;
    outline: inherit;
    text-align: center;

    @media (max-width: 949px) {
      display: none;
    }
    .tooltip-text {
      min-width: 70px;
      font-size: 12px;
      line-height: 16px;
      padding: 4px 8px;
      background-color: #0b395b;
      color: white;
      border-radius: 2px;
      position: absolute;
      bottom: -32px;
      left: 50%;
      transform: translateX(-50%);
      visibility: hidden;
    }
    &:hover .tooltip-text {
      visibility: visible;
    }
  }
`

const ComplaintsTab = styled(PaymentsTab)``
const ContactPage = props => {
  const [tabActive, setTabActive] = useState("general")
  const [tooltipText, setTooltipText] = useState("Copy email")
  const [paymentsFormSubmitted, setPaymentsFormSubmitted] = useState(false)
  const [complaintsFormSubmitted, setComplaintsFormSubmitted] = useState(false)
  return (
    <>
      <Wrapper>
        <Header>
          <h1>Contact us</h1>
          <p>Get in touch, and let us know how we can help.</p>
        </Header>
      </Wrapper>
      <SubNav>
        <TabContainerWrapper>
          <TabContainerMobile tabActive={tabActive}>
            <Tab
              className="tab-general"
              onClick={() => setTabActive("general")}
            >
              General
            </Tab>
            <Tab
              className="tab-payments"
              onClick={() => setTabActive("payments")}
            >
              Payments
            </Tab>
            <Tab
              className="tab-complaints"
              onClick={() => setTabActive("complaints")}
            >
              Complaints
            </Tab>
          </TabContainerMobile>
          <TabContainerTabletDesktop tabActive={tabActive}>
            <Tab
              className="tab-general"
              onClick={() => setTabActive("general")}
            >
              General
            </Tab>
            <Tab
              className="tab-payments"
              onClick={() => setTabActive("payments")}
            >
              Mortgage Payments
            </Tab>
            <Tab
              className="tab-complaints"
              onClick={() => setTabActive("complaints")}
            >
              Complaints
            </Tab>
          </TabContainerTabletDesktop>
        </TabContainerWrapper>
      </SubNav>
      <SubNavHr />
      {tabActive === "general" && (
        <GeneralTab>
          <h2>Ways to get in touch</h2>
          <Wrapper>
            <CardContainer>
              <a className="general-contact" href="tel:757-213-1660">
                <Card>
                  <CardImgText>
                    <svg
                      width="52"
                      height="52"
                      viewBox="0 0 52 52"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="26"
                        cy="26"
                        r="25"
                        stroke="#013A74"
                        strokeWidth="2"
                      />
                      <path
                        d="M35.8881 30.9201V33.9201C35.8892 34.1986 35.8322 34.4743 35.7206 34.7294C35.6091 34.9846 35.4454 35.2137 35.2402 35.402C35.035 35.5902 34.7927 35.7336 34.5289 35.8228C34.265 35.912 33.9855 35.9452 33.7081 35.9201C30.631 35.5857 27.6751 34.5342 25.0781 32.8501C22.6619 31.3148 20.6135 29.2663 19.0781 26.8501C17.3881 24.2413 16.3364 21.2711 16.0081 18.1801C15.9831 17.9036 16.016 17.6249 16.1046 17.3617C16.1932 17.0986 16.3357 16.8568 16.5229 16.6517C16.7101 16.4466 16.9379 16.2828 17.1919 16.1706C17.4459 16.0584 17.7205 16.0004 17.9981 16.0001H20.9981C21.4834 15.9953 21.9539 16.1672 22.3219 16.4836C22.6898 16.8001 22.9302 17.2395 22.9981 17.7201C23.1247 18.6802 23.3596 19.6228 23.6981 20.5301C23.8327 20.888 23.8618 21.277 23.782 21.651C23.7023 22.0249 23.517 22.3682 23.2481 22.6401L21.9781 23.9101C23.4017 26.4136 25.4746 28.4865 27.9781 29.9101L29.2481 28.6401C29.52 28.3712 29.8633 28.1859 30.2372 28.1062C30.6112 28.0264 31.0002 28.0556 31.3581 28.1901C32.2654 28.5286 33.2081 28.7635 34.1681 28.8901C34.6539 28.9586 35.0975 29.2033 35.4146 29.5776C35.7318 29.9519 35.9003 30.4297 35.8881 30.9201Z"
                        stroke="#013A74"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <h3 className="sans">General Contact</h3>
                    <p>Our offices are open 9:00 am – 5:00 pm ET.</p>
                  </CardImgText>

                  <CardFooter className="card-footer">
                    <p>(757) 213-1660</p>
                  </CardFooter>
                </Card>
              </a>

              <CardLink to="/newsroom">
                <Card className="press">
                  <CardImgText>
                    <svg
                      width="52"
                      height="52"
                      viewBox="0 0 52 52"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="26"
                        cy="26"
                        r="25"
                        stroke="#013A74"
                        strokeWidth="2"
                      />
                      <path
                        d="M16 17H22C23.0609 17 24.0783 17.4214 24.8284 18.1716C25.5786 18.9217 26 19.9391 26 21V35C26 34.2044 25.6839 33.4413 25.1213 32.8787C24.5587 32.3161 23.7956 32 23 32H16V17Z"
                        stroke="#013A74"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M36 17H30C28.9391 17 27.9217 17.4214 27.1716 18.1716C26.4214 18.9217 26 19.9391 26 21V35C26 34.2044 26.3161 33.4413 26.8787 32.8787C27.4413 32.3161 28.2044 32 29 32H36V17Z"
                        stroke="#013A74"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                    <h3 className="sans">Press</h3>
                    <p>Access Atlantic Bay stories, news, and resources.</p>
                  </CardImgText>

                  <CardFooter className="card-footer">
                    <p>Visit our newsroom</p>
                  </CardFooter>
                </Card>
              </CardLink>
            </CardContainer>
          </Wrapper>
          <Headquarters>
            <HeadquartersFlexContainer>
              <GatsbyImage
                image={props.data.Ocean.childImageSharp.gatsbyImageData}
                style={{
                  position: "absolute",
                  zIndex: "-10",
                  top: 0,
                  left: 0,

                  width: "100%",
                  height: "100%",
                  objectPosition: "50% 50%",
                }}
              />
              <HeadquartersCardContainer>
                <HeadquartersCard>
                  <h2>Headquarters</h2>
                  <hr className="headquarters-hr" />
                  <p>Atlantic Bay Mortgage Group</p>
                  <p>600 Lynnhaven Pkwy, Ste. 100 </p>
                  <p>Virginia Beach, VA 23452</p>
                  <a
                    href="https://www.google.com/maps/dir//Atlantic+Bay+Mortgage+Group,+600+Lynnhaven+Pkwy+Suite+100,+Virginia+Beach,+VA+23452/@36.8260187,-76.0684697,17z/data=!4m9!4m8!1m0!1m5!1m1!1s0x89baeab9514c932f:0xbaa2770cb5886c70!2m2!1d-76.066437!2d36.826014!3e0"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Get directions
                  </a>
                </HeadquartersCard>
              </HeadquartersCardContainer>
            </HeadquartersFlexContainer>
          </Headquarters>
        </GeneralTab>
      )}
      {tabActive === "payments" && (
        <PaymentsTab>
          <FormWrapper>
            {!paymentsFormSubmitted ? (
              <PaymentsForm
                paymentsFormSubmitted={paymentsFormSubmitted}
                setPaymentsFormSubmitted={setPaymentsFormSubmitted}
              />
            ) : (
              <SuccessMessage setFormIsSubmitted={setPaymentsFormSubmitted} />
            )}
          </FormWrapper>
          <WereHereToHelp>
            <h2 className="sans">We're here to help.</h2>
            <FlexButtonContainer>
              <a href="tel:888-880-4617">(888) 880-4617</a>
              <a
                className="servicing-email"
                href="mailto:servicing@atlanticbay.com"
              >
                servicing@atlanticbay.com
              </a>
              <CopyToClipboard
                className="copy-button"
                text="servicing@atlanticbay.com"
              >
                <button
                  onClick={() => {
                    setTooltipText("Email copied!")
                  }}
                  onMouseLeave={() => {
                    setTooltipText("Copy email")
                  }}
                >
                  servicing@atlanticbay.com{" "}
                  <span className="tooltip-text">{tooltipText}</span>
                </button>
              </CopyToClipboard>
            </FlexButtonContainer>
            <p>Monday – Friday 9:00 am – 5:00 pm ET</p>
          </WereHereToHelp>
        </PaymentsTab>
      )}
      {tabActive === "complaints" && (
        <ComplaintsTab>
          <FormWrapper>
            {!complaintsFormSubmitted ? (
              <ComplaintsForm
                complaintsFormSubmitted={complaintsFormSubmitted}
                setComplaintsFormSubmitted={setComplaintsFormSubmitted}
              />
            ) : (
              <SuccessMessage setFormIsSubmitted={setComplaintsFormSubmitted} />
            )}
          </FormWrapper>
        </ComplaintsTab>
      )}
    </>
  )
}

// Meta tags
export const Head = () => {
  return (
    <SEO
      description={`Have questions for us? We would love to hear from you and help you in any way that we can.`}
      title={`Contact Us | Atlantic Bay Mortgage Group®`}
      slug={`contact`}
    />
  )
}

export default ContactPage

export const query = graphql`
  query ContactPageQuery {
    Ocean: file(relativePath: { regex: "/contact/ocean-background.png/" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH)
      }
    }
  }
`
